import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {SaveOutlined} from '@material-ui/icons';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import FileSelector from '../../../shared/fileselector/FileSelector.js';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import useErrorMessage from '../../../../hooks/useErrorMessage.js';
import {arquivoParaBase64} from '../../../../utils/arquivo.js';

// O arquivo vai em base64 dentro do JSON da inscrição: o limite é o mesmo do fluxo público (5 MB).
const TAMANHO_MAXIMO = 5 * 1024 * 1024;
const FORMATOS_VALIDOS = [{format: 'PDF', mimeType: ['application/pdf', 'application/x-download', 'application/octet-stream']}];

const FormSchema = Yup.object().shape({
    idDocumento: Yup.string().required('É obrigatório'),
    arquivo: Yup.mixed().test('obrigatorio', 'É obrigatório', function obrigatorio(valor) {
        return !!valor || !!(this.options.context && this.options.context.possuiArquivo);
    })
});

const rawObject = {idDocumento: '', arquivo: null};

const useStyles = makeStyles(() => ({
    root: {
        width: 800
    },
    contentRoot: {
        overflowY: 'initial'
    }
}));

/*
 * Documento da inscrição (_formSelecionarDocumento do PrimeFaces): tipo de
 * documento, entre os do processo ainda não anexados, e o arquivo em PDF de
 * até 5 MB, enviado em base64 em anexo.arquivo. Na edição o arquivo é
 * opcional: sem um novo, fica o que já está gravado.
 */
export default function DialogDocumentoInscricao({open, onClose, title, value, documentos = [], onSubmit}) {
    const classes = useStyles();
    const showErrorMessage = useErrorMessage();
    const [lendoArquivo, setLendoArquivo] = useState(false);
    const possuiArquivo = !!value?.anexo;

    const {control, handleSubmit, reset, setValue, watch, formState: {errors, isSubmitting}} = useForm({
        resolver: (valores, contexto, opcoes) => yupResolver(FormSchema)(valores, {possuiArquivo}, opcoes),
        defaultValues: rawObject
    });
    const arquivo = watch('arquivo');

    useEffect(() => {
        if (open) {
            reset({idDocumento: value?.documento?.id ? String(value.documento.id) : '', arquivo: null});
        }
    }, [open, value]);

    /* Re-popula a cada abertura (padrão §1.7). */
    const onEntering = () => reset({idDocumento: value?.documento?.id ? String(value.documento.id) : '', arquivo: null});

    const handleClose = () => {
        onClose();
        reset(rawObject);
    };

    const aoSelecionarArquivo = async file => {
        setLendoArquivo(true);
        try {
            const conteudo = await arquivoParaBase64(file);
            setValue('arquivo', {
                nome: file.name,
                mimetype: file.type && file.type !== 'application/octet-stream' ? file.type : 'application/pdf',
                tamanho: file.size,
                arquivo: conteudo
            }, {shouldValidate: true});
        } catch (e) {
            showErrorMessage('Não foi possível ler o arquivo selecionado. Tente escolhê-lo novamente.');
        } finally {
            setLendoArquivo(false);
        }
    };

    const submit = valores => {
        const documento = documentos.find(item => String(item.id) === String(valores.idDocumento))
            || (String(value?.documento?.id) === String(valores.idDocumento) ? value.documento : {id: Number(valores.idDocumento)});
        const novo = valores.arquivo;

        onSubmit({
            ...(value || {}),
            documento,
            anexo: novo
                ? {...(value?.anexo || {}), nome: novo.nome, mimetype: novo.mimetype, tamanho: novo.tamanho, arquivo: novo.arquivo}
                : value.anexo
        });
        handleClose();
    };

    /* O diálogo fica dentro do form da página na árvore do React: o submit não pode subir até ele. */
    const aoEnviar = evento => {
        evento.stopPropagation();
        handleSubmit(submit)(evento);
    };

    const nomeArquivo = arquivo ? arquivo.nome : value?.anexo?.nome;

    return (
        <Dialog open={open} onClose={handleClose} TransitionProps={{onEntering}} scroll='body'
                aria-labelledby='dialog-documento-inscricao-titulo' classes={{paper: classes.root}}>
            <DialogTitle id='dialog-documento-inscricao-titulo'>{title}</DialogTitle>
            <form noValidate autoComplete='off' onSubmit={aoEnviar}>
                <DialogContent classes={{root: classes.contentRoot}}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <LabelValue label='Tipo de documento *'>
                                <FormControl variant='outlined' fullWidth error={!!errors.idDocumento}>
                                    <Controller
                                        name='idDocumento'
                                        control={control}
                                        render={({field}) => (
                                            <Select {...field} displayEmpty>
                                                <MenuItem value='' disabled><em>Selecione...</em></MenuItem>
                                                {documentos.map(documento => (
                                                    <MenuItem key={documento.id} value={String(documento.id)}>{documento.nome}</MenuItem>
                                                ))}
                                            </Select>
                                        )}/>
                                    {!!errors.idDocumento && <FormHelperText error>{errors.idDocumento.message}</FormHelperText>}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12}>
                            <LabelValue label={possuiArquivo ? 'Arquivo (PDF até 5 MB)' : 'Arquivo (PDF até 5 MB) *'}>
                                <FileSelector
                                    htmlFor='arquivoDocumentoInscricao'
                                    buttonText='Selecionar'
                                    validFormats={FORMATOS_VALIDOS}
                                    maxSize={TAMANHO_MAXIMO}
                                    onFile={nomeArquivo ? {nome: nomeArquivo} : null}
                                    onSelect={aoSelecionarArquivo}
                                    onRemove={() => setValue('arquivo', null, {shouldValidate: true})}
                                    error={!!errors.arquivo}
                                    errorMessage={errors.arquivo?.message}/>
                            </LabelValue>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <TextButton onClick={handleClose}>Fechar</TextButton>
                    <ContainedButton type='submit' startIcon={<SaveOutlined/>} disabled={isSubmitting || lendoArquivo}>
                        Salvar
                    </ContainedButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}

DialogDocumentoInscricao.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    value: PropTypes.object,
    documentos: PropTypes.array,
    onSubmit: PropTypes.func.isRequired
};
